import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as AddIcon } from '@assets/images/Icons/Add.svg'
import { ReactComponent as CheckmarkMenuIcon } from '@assets/images/Icons/Checkmark_menu.svg'
import type { SelectChipProps } from './SelectChip.types'
import './SelectChip.css'

type MenuPosition = {
  top: number
  left: number
}

const MENU_GAP = 8

const SelectChip: React.FC<SelectChipProps> = ({
  label,
  options,
  value,
  defaultValue = null,
  onChange,
  icon,
  count,
  darkMode = false,
  open,
  defaultOpen = false,
  onOpenChange,
  previewState,
  disabled = false,
  className = '',
  ariaLabel,
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const [internalValue, setInternalValue] = useState<string | null>(
    defaultValue,
  )
  const [position, setPosition] = useState<MenuPosition | null>(null)

  const isPreview = previewState != null
  const isOpenControlled = open !== undefined
  const isValueControlled = value !== undefined

  const selectedValue = isValueControlled ? (value ?? null) : internalValue
  const hasSelection = selectedValue != null && selectedValue !== ''

  const isOpen = isPreview
    ? previewState === 'open'
    : isOpenControlled
      ? open
      : internalOpen

  const isActive = isPreview
    ? previewState === 'selected' || previewState === 'open'
    : isOpen || hasSelection

  const resolvedCount =
    count ?? (hasSelection || (isPreview && isActive) ? 1 : 0)
  const showCount = isActive && resolvedCount > 0

  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (!isOpenControlled) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isOpenControlled, onOpenChange],
  )

  const setValue = useCallback(
    (nextValue: string | null): void => {
      if (!isValueControlled) {
        setInternalValue(nextValue)
      }
      onChange?.(nextValue)
    },
    [isValueControlled, onChange],
  )

  const updatePosition = useCallback((): void => {
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) {
      return
    }

    const triggerRect = trigger.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()

    let left = triggerRect.left - 1
    if (left + menuRect.width > window.innerWidth - MENU_GAP) {
      left = triggerRect.right - menuRect.width
    }
    if (left < MENU_GAP) {
      left = MENU_GAP
    }

    let top = triggerRect.bottom + MENU_GAP
    if (top + menuRect.height > window.innerHeight - MENU_GAP) {
      top = triggerRect.top - MENU_GAP - menuRect.height
    }

    setPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (isPreview || !isOpen) {
      setPosition(null)
      return
    }

    updatePosition()
  }, [isOpen, isPreview, updatePosition, options, selectedValue])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handleReposition = (): void => updatePosition()

    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, isPreview, updatePosition])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        !rootRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [isOpen, isPreview, setOpen])

  useEffect(() => {
    if (isPreview || !isOpen) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isPreview, setOpen])

  const classNames = [
    'ds-select-chip',
    isActive ? 'ds-select-chip--active' : '',
    isOpen ? 'ds-select-chip--open' : '',
    darkMode ? 'ds-select-chip--dark' : '',
    isPreview ? 'ds-select-chip--preview' : '',
    previewState ? `ds-select-chip--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const menuClassNames = [
    'ds-select-chip__menu',
    darkMode ? 'ds-select-chip__menu--dark' : '',
    !isPreview && isOpen ? 'ds-select-chip__menu--portal' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const triggerLabel = showCount ? `${label} (${resolvedCount})` : label

  const menuContent = options.map((option) => {
    const selected = selectedValue === option.id
    return (
      <button
        key={option.id}
        type="button"
        role="menuitemradio"
        aria-checked={selected}
        className={[
          'ds-select-chip__option',
          selected ? 'ds-select-chip__option--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        tabIndex={isPreview ? -1 : undefined}
        onClick={() => {
          if (isPreview) {
            return
          }
          setValue(option.id)
          setOpen(false)
        }}
      >
        <span className="ds-select-chip__option-inner">
          <span className="ds-select-chip__check" aria-hidden="true">
            <CheckmarkMenuIcon />
          </span>
          <span className="ds-select-chip__option-label">{option.label}</span>
        </span>
      </button>
    )
  })

  return (
    <div ref={rootRef} className={classNames}>
      <button
        ref={triggerRef}
        type="button"
        className="ds-select-chip__trigger"
        aria-label={ariaLabel ?? label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled}
        tabIndex={isPreview ? -1 : undefined}
        onClick={() => {
          if (isPreview || disabled) {
            return
          }
          setOpen(!isOpen)
        }}
      >
        <span className="ds-select-chip__icon" aria-hidden="true">
          {icon ?? <AddIcon className="ds-select-chip__icon-svg" />}
        </span>
        <span className="ds-select-chip__label">{triggerLabel}</span>
      </button>

      {isOpen && isPreview ? (
        <div className={menuClassNames} role="menu" aria-label={label}>
          {menuContent}
        </div>
      ) : null}

      {isOpen && !isPreview
        ? createPortal(
            <div
              ref={menuRef}
              className={menuClassNames}
              role="menu"
              aria-label={ariaLabel ?? label}
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                visibility: position ? 'visible' : 'hidden',
              }}
            >
              {menuContent}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

export default SelectChip
